import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ChevronLeft, Check, AlertCircle } from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";
import { useTranslation } from "@/i18n";
import PasswordInput from "../components/shared/PasswordInput";
import { passwordFailures } from "../utils/passwordRules";
import { resetErrorKey } from "../utils/resetErrors";

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { adoptSession } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError(t('reset.errInvalid'));
    }
  }, [token, t]);


  // Shared rules - see utils/passwordRules. Keys are mapped to readable text
  // here; printing the raw keys would show the user "special".
  const PW_LABELS = {
    len: t('login.pwMin') || 'At least 8 characters',
    up: t('login.pwUpper') || 'One uppercase letter',
    low: t('login.pwLower') || 'One lowercase letter',
    num: t('login.pwNumber') || 'One number',
    special: t('login.pwSpecial') || 'One special character (! @ # $ ...)',
  };
  const passwordErrors = password
    ? passwordFailures(password).map((k) => PW_LABELS[k] || k)
    : [];
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordErrors.length > 0) {
      toast.error(t('reset.fixRequirements'));
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(t('settings.passwordsNoMatch'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t('reset.success'));
      // The backend signs them in as part of the reset, so go straight into
      // the app rather than bouncing back to a login form.
      if (adoptSession && adoptSession(res.data)) {
        navigate("/", { replace: true });
        return;
      }
    } catch (err) {
      // A code from the server, shown in the household's own language.
      const message = t(resetErrorKey(err));
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !success) {
    return (
      <div className="app-container px-8 flex flex-col justify-center min-h-screen">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{t('reset.linkInvalid')}</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/forgot-password')}
            className="clean-btn-primary"
            data-testid="request-new-reset"
          >
            {t('reset.requestNewLink')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="app-container px-8 flex flex-col justify-center min-h-screen">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{t('reset.passwordResetTitle')}</h2>
          <p className="text-gray-500 mb-6">{t('reset.successBody')}</p>
          <button 
            onClick={() => navigate('/login')}
            className="clean-btn-primary"
            data-testid="go-to-login"
          >
            {t('login.signIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container px-8 flex flex-col justify-center min-h-screen">
      <div className="mb-8">
        <button onClick={() => navigate('/login')} className="flex items-center text-gray-500 hover:text-black">
          <ChevronLeft size={20} />
          <span>{t('forgot.backToLogin')}</span>
        </button>
      </div>
      
      <div className="mb-8 text-center relative z-10">
        <h1 className="text-3xl font-bold tracking-tight mb-2">{t('reset.newPasswordTitle')}</h1>
        <p className="text-gray-500">{t('reset.createStrong')}</p>
      </div>

      <div className="relative z-10">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-500 mb-1 block">{t('settings.newPassword')}</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputClassName="clean-input"
              placeholder="••••••••"
              required
              autoFocus
              data-testid="new-password-input"
            />
            {password && passwordErrors.length > 0 && (
              <div className="mt-2 text-xs">
                <p className="text-gray-500 mb-1">{t('reset.mustInclude')}</p>
                {passwordErrors.map((err, i) => (
                  <p key={i} className="text-red-500">• {err}</p>
                ))}
              </div>
            )}
            {password && passwordErrors.length === 0 && (
              <p className="mt-2 text-xs text-green-600">✓ {t('settings.passwordMeetsAll')}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500 mb-1 block">
              {t('reset.confirmPassword')}
            </label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputClassName="clean-input"
              placeholder="••••••••"
              required
              data-testid="confirm-password-input"
            />
            {confirmPassword && (
              <p className={`mt-2 text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                {passwordsMatch ? `✓ ${t('reset.passwordsMatch')}` : `✗ ${t('reset.passwordsDiffer')}`}
              </p>
            )}
          </div>

          <button 
            type="submit" 
            className="clean-btn-primary w-full"
            disabled={submitting || passwordErrors.length > 0 || !passwordsMatch}
            data-testid="reset-password-btn"
          >
            {submitting ? t('reset.resetting') : t('forgot.resetPassword')}
          </button>
        </form>
      </div>
    </div>
  );
};
